import React from 'react'
import styled from 'styled-components'
import { Box } from '../../../../atoms'
import { PDFReportHeader } from '../../../../../print'
import { PDFMapView } from './PDFMapView'
import type { PDFCoverPageProps } from '../ExportPDFModal/types'

const DEFAULT_ACCENT_COLOR = '#0294df'
const DEFAULT_TEXT_COLOR = '#1e293b'
const DEFAULT_SUBTEXT_COLOR = '#64748b'

const CoverWrapper = styled.div.attrs({
  className: 'print-page-preview',
})`
  display: flex;
  flex-direction: column;
  height: 1670px;
  min-height: 1670px;
`

const CoverContent = styled.div.attrs({
  className: 'print-cover-content',
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 26px;
  height: 100%;
`

const TitleSection = styled.div`
  flex-shrink: 0;
`

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px 0;
`

const MapSection = styled.div`
  margin-bottom: 20px;
`

const Title = styled.h1<{ $textColor: string }>`
  font-size: 28px;
  font-weight: 700;
  color: ${({ $textColor }) => $textColor};
  margin: 0 0 10px 0;
  line-height: 1.2;
`

const AccentLine = styled.div<{ $accentColor: string }>`
  width: 64px;
  height: 3px;
  background-color: ${({ $accentColor }) => $accentColor};
  margin-bottom: 12px;
  border-radius: 2px;
`

const Subtitle = styled.p<{ $subtextColor: string }>`
  font-size: 15px;
  color: ${({ $subtextColor }) => $subtextColor};
  margin: 0;
  line-height: 1.5;
`

const Footer = styled.div`
  padding-top: 20px;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-shrink: 0;
`

const CompanyInfoSection = styled.div`
  display: flex;
  flex-direction: column;
`

const CompanyName = styled.div<{ $textColor: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $textColor }) => $textColor};
  margin-bottom: 3px;
`

const CompanyAddress = styled.div<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

const DateSection = styled.div`
  text-align: right;
`

const DateLabel = styled.div<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
  margin-bottom: 3px;
`

const DateValue = styled.div<{ $textColor: string }>`
  font-size: 10px;
  font-weight: 500;
  color: ${({ $textColor }) => $textColor};
`

/**
 * Cover page component for PDF exports
 * Displays company logo, title, custom content, and footer
 */
export const PDFCoverPage: React.FC<PDFCoverPageProps> = ({
  title,
  subtitle,
  company,
  user,
  accentColor = DEFAULT_ACCENT_COLOR,
  textColor = DEFAULT_TEXT_COLOR,
  subtextColor = DEFAULT_SUBTEXT_COLOR,
  customContent,
  markers,
  showMap = true,
}) => {
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Convert user/company to the format expected by PDFReportHeader
  const headerUser = user
    ? {
        name: user.name,
        email: user.email,
        phone: user.phone,
        photoUrl: user.photo,
      }
    : undefined

  const headerCompany = company
    ? {
        name: company.name,
        address: company.address,
        logoUrl: company.logo,
      }
    : undefined

  return (
    <CoverWrapper>
      <CoverContent>
        {/* Header with company logo and user info */}
        {(headerUser || headerCompany) && (
          <PDFReportHeader
            user={headerUser || { name: 'User' }}
            company={headerCompany || { name: 'Company' }}
            accentColor={accentColor}
            textColor={textColor}
            subtextColor={subtextColor}
          />
        )}

        {/* Title section */}
        <TitleSection>
          <Title $textColor={textColor}>{title}</Title>
          <AccentLine $accentColor={accentColor} />
          {subtitle && <Subtitle $subtextColor={subtextColor}>{subtitle}</Subtitle>}
        </TitleSection>

        {/* Content section - centered vertically */}
        <ContentSection>
          {/* Map section if markers provided */}
          {showMap && markers && markers.length > 0 && (
            <MapSection>
              <PDFMapView markers={markers} height="500px" accentColor={accentColor} />
            </MapSection>
          )}

          {/* Custom content if provided */}
          {customContent && <Box style={{ marginBottom: '20px' }}>{customContent}</Box>}
        </ContentSection>

        {/* Footer */}
        <Footer>
          {company && (
            <CompanyInfoSection>
              <CompanyName $textColor={textColor}>{company.name}</CompanyName>
              {company.address && (
                <CompanyAddress $subtextColor={subtextColor}>{company.address}</CompanyAddress>
              )}
            </CompanyInfoSection>
          )}

          <DateSection>
            <DateLabel $subtextColor={subtextColor}>Generated on</DateLabel>
            <DateValue $textColor={textColor}>{today}</DateValue>
          </DateSection>
        </Footer>
      </CoverContent>
    </CoverWrapper>
  )
}
